// Character Registry modal — the per-client curated character list (Run 2,
// Builds 2+3). Opened by clicking the Character column header in the script
// editor; scoped to that script's client.
//
// The registry is a picker, not a source of truth (D-011): renaming here never
// rewrites a script body, and the modal says so plainly on every rename.
//
// §3.1 — a casting note is never a casting. A character with no defaultTalent is
// UNCAST whatever its note says; the note renders as muted italics behind an
// explicit "Uncast" marker so nobody can mistake it for a booked person.
// HD-7: on src/ui (Modal, Input, Select, Button) and role tokens; every call and data-testid is as before. Delete now
// asks first (two-step destructive confirm).

import React, { useState, useEffect, useMemo } from 'react';
import { Archive, ArchiveRestore, ChevronDown, ClipboardPaste, Pencil, Plus, Trash2 } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import Skeleton from '../ui/Skeleton';
import Textarea from '../ui/Textarea';
import { useConfirm } from '../ui/Confirm';
import { clean } from '../shell/copy';

const RENAME_WARNING = 'Renaming affects new script lines only. Lines already written keep the old name.';

export default function CharacterRegistryModal({
  clientId, clientName, onClose,
  canEdit,       // add / paste / rename / archive / restore / delete
  canMapTalent,  // set defaultTalentId (Producer / Power User / Admin)
  onChanged,     // notify the editor so the cell picker refetches
}) {
  const confirm = useConfirm();
  const [rows,       setRows]       = useState(null);   // null = loading
  const [talents,    setTalents]    = useState([]);     // active Talent-role members
  const [errMsg,     setErrMsg]     = useState('');
  const [notice,     setNotice]     = useState('');
  const [newName,    setNewName]    = useState('');
  const [pasteOpen,  setPasteOpen]  = useState(false);
  const [pasteText,  setPasteText]  = useState('');
  const [busy,       setBusy]       = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [editName,   setEditName]   = useState('');
  const [showArchived, setShowArchived] = useState(false);

  async function load() {
    try {
      const r = await api.get(`/api/client-characters?clientId=${clientId}`);
      setRows(r.data.characters || []);
    } catch (e) {
      setErrMsg(clean(e.response?.data?.error) || 'The character list did not load. Close this and try again.');
      setRows([]);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientId]);
  useEffect(() => {
    if (!canMapTalent) return;
    api.get('/api/team-members')
      .then(r => setTalents((r.data || []).filter(m =>
        m.isActive !== false && m.roles?.some(x => x.role === 'Talent'))))
      .catch(() => {});
  }, [canMapTalent]);

  const changed = () => { onChanged?.(); };

  async function run(fn, okMsg) {
    setBusy(true); setErrMsg(''); setNotice('');
    try { await fn(); if (okMsg) setNotice(okMsg); await load(); changed(); }
    catch (e) { setErrMsg(clean(e.response?.data?.error) || 'That did not work. Try again.'); }
    finally { setBusy(false); }
  }

  const addSingle = () => {
    const name = newName.trim();
    if (!name) return;
    run(async () => {
      await api.post('/api/client-characters', { clientId, name });
      setNewName('');
    }, `Added "${name}".`);
  };

  const importPaste = () => run(async () => {
    const r = await api.post('/api/client-characters/import', { clientId, text: pasteText });
    const { addedCount, alreadyPresentCount, rejected = [] } = r.data;
    setNotice(
      `${addedCount} added, ${alreadyPresentCount} already present` +
      (rejected.length ? `, ${rejected.length} rejected (no letters)` : '') + '.');
    setPasteText(''); setPasteOpen(false);
  });

  const saveRename = (row) => {
    const name = editName.trim();
    if (!name || name === row.name) { setEditingId(null); return; }
    run(async () => {
      await api.patch(`/api/client-characters/${row.id}`, { name });
      setEditingId(null);
    }, `Renamed to "${name}". ${RENAME_WARNING}`);
  };

  const setTalent = (row, talentId) => run(async () => {
    await api.patch(`/api/client-characters/${row.id}`, { defaultTalentId: talentId || null });
  });

  const archive = (row) => run(async () => {
    await api.post(`/api/client-characters/${row.id}/archive`);
  }, `Archived "${row.name}".`);

  const restore = (row) => run(async () => {
    await api.post(`/api/client-characters/${row.id}/restore`);
  }, `Restored "${row.name}".`);

  const remove = async (row) => {
    const ok = await confirm({
      title: `Delete ${row.name}?`,
      body: 'Only possible while the character has no talent and is used in no script. Archive keeps it instead.',
      confirmLabel: 'Delete character',
      destructive: true,
    });
    if (!ok) return;
    run(async () => {
      await api.delete(`/api/client-characters/${row.id}`);
    }, `Deleted "${row.name}".`);
  };

  const live     = useMemo(() => (rows || []).filter(r => !r.isArchived), [rows]);
  const archived = useMemo(() => (rows || []).filter(r =>  r.isArchived), [rows]);

  // §3.1 — the three talent states, visually distinct. `uncastChip` is the
  // explicit marker; the note is muted italics BEHIND it, never a bare name.
  // DEF-108: the chip is a button — clicking it focuses and opens the row's
  // default-talent picker (the same select). It never removes anything.
  const uncastChip = (
    <button
      type="button"
      className="ui-tag"
      data-testid="registry-uncast-chip"
      disabled={!canMapTalent}
      title={canMapTalent ? 'Uncast: click to pick the standing talent for this character' : 'Uncast: a Producer, Power User or Admin maps talent'}
      onClick={e => {
        e.stopPropagation();
        const sel = e.currentTarget.parentElement?.querySelector('select');
        if (sel) { sel.focus(); try { sel.showPicker?.(); } catch { /* needs a user gesture in some browsers — focus is enough */ } }
      }}
    >Uncast</button>
  );

  function talentCell(row) {
    const picker = canMapTalent && (
      <Select
        aria-label={`Default talent for ${row.name}`}
        value={row.defaultTalentId || ''}
        disabled={busy}
        onChange={e => setTalent(row, e.target.value)}
        title="Standing cast for this character: the picker lists Talent-role members only"
        placeholder="No default talent"
        options={[
          ...talents.map(t => ({ value: t.id, label: t.name })),
          // keep an unlisted current value visible rather than silently blanking it
          ...(row.defaultTalentId && !talents.some(t => t.id === row.defaultTalentId) ? [{ value: row.defaultTalentId, label: row.defaultTalent?.name || 'Unknown member' }] : []),
        ]}
      />
    );
    if (row.defaultTalentId) {
      return <span className="ui-cluster ui-cluster--nowrap">{picker || <span className="ui-truncate ui-text-2">{row.defaultTalent?.name}</span>}</span>;
    }
    return (
      <span className="ui-cluster ui-cluster--nowrap">
        {picker}
        {uncastChip}
        {row.castingNote && (
          <span className="ui-meta ui-truncate" title={`Writers' note, not a casting: "${clean(row.castingNote)}"`}>
            <em>Note: {clean(row.castingNote)}</em>
          </span>
        )}
      </span>
    );
  }

  function liveRow(row) {
    const isEditing = editingId === row.id;
    return (
      <div key={row.id} className={`ui-regrow${canEdit ? ' ui-regrow--edit' : ''}`}>
        {isEditing ? (
          <Input
            aria-label={`New name for ${row.name}`}
            autoFocus value={editName}
            onChange={e => setEditName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveRename(row); if (e.key === 'Escape') setEditingId(null); }}
          />
        ) : (
          <span className="ui-truncate">{row.name}</span>
        )}
        {talentCell(row)}
        {canEdit && (
          <span className="ui-cluster ui-cluster--nowrap ui-cluster--end">
            {isEditing ? (
              <>
                <Button variant="primary" disabled={busy} onClick={() => saveRename(row)}>Save</Button>
                <Button variant="quiet" onClick={() => setEditingId(null)}>Cancel</Button>
              </>
            ) : (
              <>
                <Button variant="quiet" iconOnly icon={Pencil} label={`Rename ${row.name}`} disabled={busy}
                  onClick={() => { setEditingId(row.id); setEditName(row.name); setNotice(RENAME_WARNING); }} />
                <Button variant="quiet" iconOnly icon={Archive} label={`Archive ${row.name}: reversible, keeps history, frees the name`} disabled={busy}
                  onClick={() => archive(row)} />
                <Button variant="danger" iconOnly icon={Trash2} label={`Delete ${row.name}: only possible when unmapped and used in no script`} disabled={busy}
                  onClick={() => remove(row)} />
              </>
            )}
          </span>
        )}
      </div>
    );
  }

  return (
    <Modal
      size="medium"
      title={`Characters for ${clean(clientName) || 'this client'}`}
      meta="The curated list the Character cell suggests from. Picking one still writes the plain name into the script."
      onClose={onClose}
    >
      <div className="ui-stack" data-testid="character-registry-modal">
        {errMsg && <p className="ui-p ui-dense ui-text-danger" role="alert">{errMsg}</p>}
        {notice && <p className="ui-p ui-dense ui-text-2" role="status" data-testid="registry-notice">{notice}</p>}

        {canEdit && (
          <div className="ui-stack">
            <div className="ui-cluster ui-cluster--nowrap">
              <Input
                className="ui-grow"
                aria-label="New character"
                placeholder="Add a character"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSingle(); } }}
                data-testid="registry-add-input"
              />
              <Button variant="primary" icon={Plus} disabled={busy || !newName.trim()} onClick={addSingle}>Add</Button>
              <Button variant="secondary" icon={ClipboardPaste} aria-expanded={pasteOpen} onClick={() => setPasteOpen(v => !v)} data-testid="registry-paste-toggle">
                {pasteOpen ? 'Hide paste' : 'Paste a list'}
              </Button>
            </div>
            {pasteOpen && (
              <div className="ui-stack">
                <Textarea
                  aria-label="Characters, one per line"
                  rows={6} placeholder="One character per line"
                  value={pasteText} onChange={e => setPasteText(e.target.value)}
                  data-testid="registry-paste-textarea"
                />
                <div className="ui-cluster ui-cluster--end">
                  <Button variant="primary" disabled={busy || !pasteText.trim()} onClick={importPaste} data-testid="registry-paste-import">
                    Import
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {rows === null ? (
          <Skeleton variant="line" width="60%" label="Loading the character list" />
        ) : live.length === 0 ? (
          <p className="ui-p ui-dense ui-text-2">No characters yet.{canEdit ? ' Add one above or paste the client’s list.' : ''}</p>
        ) : (
          <div className="ui-list" data-testid="registry-live-list">
            {live.map(liveRow)}
          </div>
        )}

        {archived.length > 0 && (
          <div className="ui-stack">
            <button type="button" className="ui-disclosure ui-dense" aria-expanded={showArchived} onClick={() => setShowArchived(v => !v)}>
              <Icon icon={ChevronDown} />Archived <span className="ui-meta ui-num">{archived.length}</span>
            </button>
            {showArchived && (
              <div className="ui-list">
                {archived.map(row => (
                  <div key={row.id} className="ui-regrow ui-regrow--archived">
                    <span className="ui-truncate ui-text-3">{row.name}</span>
                    {canEdit && (
                      <Button variant="quiet" icon={ArchiveRestore} disabled={busy} onClick={() => restore(row)}>Restore</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
